"use client";

import { Button } from "@/_components/lib/ui/button";
import { KnootLottie } from "@/_components/ui/lotties/Lottie";
import { Center, Dialog, HStack, Text, VStack } from "@chakra-ui/react";
import { useMemo } from "react";
import { useTranslations } from "next-intl";

export interface PurchaseSuccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productName: string;
  totalAmount: string;
  currencyCode?: string;
  onConfirm?: () => void;
}

function createMoneyFormatter(currencyCode: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function PurchaseSuccessDialog({
  open,
  onOpenChange,
  productName,
  totalAmount,
  currencyCode = "USD",
  onConfirm,
}: PurchaseSuccessDialogProps) {
  const t = useTranslations("pricing.purchase_success");
  const currencyFormatter = useMemo(
    () => createMoneyFormatter(currencyCode),
    [currencyCode],
  );

  const formattedTotal = useMemo(() => {
    const numericAmount = Number.parseFloat(totalAmount);
    if (!Number.isFinite(numericAmount)) {
      return totalAmount;
    }

    return currencyFormatter.format(numericAmount);
  }, [totalAmount, currencyFormatter]);

  const handleConfirm = () => {
    onConfirm?.();
    onOpenChange(false);
  };

  return (
    <Dialog.Root
      lazyMount
      open={open}
      onOpenChange={(event) => onOpenChange(event.open)}
      placement="center"
      size="sm"
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content rounded="2xl" shadow="lg" p={0}>
          <Dialog.Body p={4}>
            <VStack gap={4} align="stretch" textAlign="center">
              <Center>
                <KnootLottie
                  animationName="successCheck"
                  height="120px"
                  width="120px"
                  loop={false}
                />
              </Center>

              <Text
                textStyle="lg"
                fontWeight="semibold"
                color="primary.600"
                textTransform="uppercase"
              >
                {t("eyebrow")}
              </Text>

              <Text textStyle="2xl" fontWeight="bold" color="neutral.900">
                {t("title")}
              </Text>

              <Text textStyle="md" fontWeight="medium" color="neutral.500">
                {t("description_1")}
              </Text>
              <Text
                textStyle="md"
                fontWeight="medium"
                color="neutral.500"
                mt={-2}
              >
                {t("description_2")}
              </Text>

              <VStack
                mt={4}
                align="stretch"
                rounded="xl"
                borderWidth="1px"
                borderColor="neutral.200"
                overflow="hidden"
              >
                <HStack p={2} justify="space-between">
                  <Text
                    textStyle="md"
                    color="neutral.500"
                    fontWeight="semibold"
                  >
                    {t("product_label")}
                  </Text>
                  <Text textStyle="md" color="neutral.900" fontWeight="bold">
                    {productName}
                  </Text>
                </HStack>
                <HStack
                  p={2}
                  justify="space-between"
                  borderTopWidth="1px"
                  borderColor="neutral.200"
                >
                  <Text
                    textStyle="md"
                    color="neutral.500"
                    fontWeight="semibold"
                  >
                    {t("total_label")}
                  </Text>
                  <Text textStyle="md" color="neutral.900" fontWeight="bold">
                    {formattedTotal}
                  </Text>
                </HStack>
              </VStack>

              <Button
                mt={6}
                w="full"
                size="lg"
                rounded="lg"
                colorPalette="primary"
                onClick={handleConfirm}
              >
                {t("confirm")}
              </Button>
            </VStack>
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
